import * as Sentry from '@sentry/svelte';
import { invalidateAll } from '$app/navigation';
import { mount } from "svelte";
import { PUBLIC_DOMAIN } from '$env/static/public';

import { patchActiveUserDataAsync } from './activeUserData.svelte.js';
import { applyTooltips } from './tooltip.js';
import { mapDisplayMessage  } from './Map.svelte';
import BeenButton from "./BeenButton.svelte";
import FlyableText from "./FlyableText.svelte";
import { reg_bbox } from '$lib/static/reg_bbox.js';
import { uuid } from '$lib/app/uuid.js'


export function server_name(x) {
    if(x) {
        if(!x.startsWith('/')) {
            x = '/' + x;
        }
    } else {
        x = '';
    }
    //return "https://" + ((x.startsWith('/staticmap')) ? ("staticmap."+PUBLIC_DOMAIN) : PUBLIC_DOMAIN) + x;
    const retval = "https://" + PUBLIC_DOMAIN + x;
    return retval;
}


export const title_options = [
    { value: null, label: 'Not set' },
    { value: 'Mr', label: 'Mr' },
    { value: 'Mrs', label: 'Mrs' },
    { value: 'Miss', label: 'Miss' },
    { value: 'Dr', label: 'Dr' },
    { value: 'Prof', label: 'Prof' },
    { value: 'Capt', label: 'Capt' },
    { value: 'Rev', label: 'Rev' },
];


export const visibility_options = [
    { value: 0, label: 'Everyone' },
    { value: 1, label: 'Ask Google not to index' },
    { value: 2, label: 'Logged-in users' },
    { value: 3, label: 'Me only' },
];


export function http_status_text(code = null, long = false) {
    // List of HTTP status codes.
    const statuslist = {
        '100': 'Continue',
        '101': 'Switching Protocols',
        '200': 'OK',
        '201': 'Created',
        '202': 'Accepted',
        '203': 'Non-Authoritative Information',
        '204': 'No Content',
        '205': 'Reset Content',
        '206': 'Partial Content',
        '300': 'Multiple Choices',
        '302': 'Found',
        '303': 'See Other',
        '304': 'Not Modified',
        '305': 'Use Proxy',
        '400': 'Bad Request',
        '401': 'Unauthorized',
        '402': 'Payment Required',
        '403': 'Forbidden',
        '404': 'Not Found',
        '405': 'Method Not Allowed',
        '406': 'Not Acceptable',
        '407': 'Proxy Authentication Required',
        '408': 'Request Timeout',
        '409': 'Conflict',
        '410': 'Gone',
        '411': 'Length Required',
        '412': 'Precondition Failed',
        '413': 'Request Entity Too Large',
        '414': 'Request-URI Too Long',
        '415': 'Unsupported Media Type',
        '416': 'Requested Range Not Satisfiable',
        '417': 'Expectation Failed',
        '500': 'Internal Server Error',
        '501': 'Not Implemented',
        '502': 'Bad Gateway',
        '503': 'Service Unavailable',
        '504': 'Gateway Timeout',
        '505': 'HTTP Version Not Supported',
        '506': 'Variant Also Negotiates',
        '507': 'Insufficient Storage',
        '508': 'Loop Detected',
        '510': 'Not Extended',
        '511': 'Network Authentication Required',
    };

    // Convert the code to a string.
    code = String(code);

    // Determine if it exists in the object.
    if (code == '200') {
        return 'OK';
    } else if (statuslist.hasOwnProperty(code)) {
        // Return the status text.
        return long ? ('Error ' + code + ": " + statuslist[code]) : statuslist[code];
    } else {
        // If it doesn't exist, degrade by returning the code.
        return long ? ('Error ' + code) : 'Unknown Error';
    }
}


// Converts fetch error to accurate and user-friendly
export function user_error(err) {
    if(typeof window.navigator === 'object' && !window.navigator.onLine) {
        err = 'No Internet Connection';
    } else if(typeof err === 'string') {
        if(err.match(/Failed.*fetch/i)) {
            err = 'Network error';
        } 
    }
    return err;
}


export function get_random_realname() {
    const random_realnames = ["Béatrice Leblanc", "Anaïs Lane", "Gauthier Richard", "Sacha Deschamps", "Véronique Bélanger", "Benoît Parris", "Éliane Gagnon",
        "Angèle Bruguière", "Maud Hérisson", "Angélique Vérany", "Marie-Françoise Mossé", "Marie-Noëlle Matthieu", "Ève Escoffier", "Albane Badeaux", "Ivanna Lécuyer",
        "Hélène Delannoy", "Maéva Dimont", "Éva Jaccoud", "Jérôme Prudhomme", "Isaïe Thibault", "Gaétan Périer", "Benoît Jégou", "Jean-Luc Girard", "Jean-Pierre Beauchamp",
        "Jean-Joël Benett", "Milovan Kopatić", "Bratislav Baraš", "Dragoslav Kroš"];

    return random_realnames[Math.floor(Math.random() * random_realnames.length)];
}


export function handleFetchReject(error) {
    return Promise.resolve({error: user_error(error.message)});
}


export function handleFetch(response) {
    if (response.status == 200) {
        return response.json().then(json => {
            // the status was ok and there is a json body
            //console.log('status 200 + json');
            if(Array.isArray(json) || typeof json !== 'object') {
                return Promise.resolve(json);
            }
            if(!json?.message) json.message = 'OK';
            if(!json?.status) json.status = 200;
            return Promise.resolve(json);
        }).catch(err => {
            // the status was ok but there is no json body
            //console.log('status 200, no json');
            return Promise.resolve({status: response.status, message: ((response.statusText || null) ?? 'OK')});
        });
    } else {
        if(response.status == 401) {
            Sentry.captureMessage('handleFetch response.status 401: calling invalidateAll()');
            invalidateAll();
        }
        return response.json().catch(err => {
            //console.log('error status '+response.status+', no json. response.statusText='+response.statusText+", status_text="+http_status_text(response.status));
            //console.log("<"+(response.statusText ?? http_status_text(response.status))+">");
            // the status was not ok and there is no json body
            return Promise.resolve({status: response.status, error: ((response.statusText || null) ?? http_status_text(response.status, true))});
        }).then(json => {
            //console.log('error status, json');
            if(!json?.error) { 
                if(json?.message) { // svelte no-brainer & compatibility
                    json.error = json?.message;
                } else {
                    json.error = http_status_text(response?.status, true);
                }
            }
            if(!json?.status) json.status = response?.status;
            // the status was not ok but there is a json body
            return Promise.resolve(json);
        });
    }
}


// clean: if response.ok, don't alter return json
// (don't add .status/.message), and even return
//  arrays or primitives as is
export async function handle_fetch(response, {clean} = {}) {
    let json = null;

    try {
        json = await response.json();

        if (response.ok && clean) {
            return json;
        }

        if (!json || typeof json !== 'object' || Array.isArray(json)) {
            json = null;     // null json is Unexpected JSON format indicator
            throw new Error('Unexpected JSON format');
        }
    } catch (err) {
        Sentry.captureMessage('Failed to parse or unexpected JSON format', { extra: { response, err } });
    }

    // Assign parsed JSON (if valid), otherwise keep ret {}
    const ret = json || {};
    ret.status = ret.status || response.status;

    if (response.ok) { // Covers all 2xx statuses
        if(json) {
            delete ret.error;
            ret.message = ret.message || 'OK';
        } else {
            // Override 2xx with 500 on JSON failure
            ret.error = 'Failed to parse server response';
            ret.status = 500;
        }
    } else {
        if (response.status == 401) {
            Sentry.captureMessage('handleFetch response.status 401: calling invalidateAll()');
            invalidateAll();
        }
        ret.error = ret.error || ret.message || http_status_text(response.status, true);
    }
    return ret;
}

/*
export async function toggle_visit(osm_uid, {been, hide, transit} = {}) {
    //e.preventDefault();
    //disabled = true;

    if(!osm_uid) {
        Sentry.captureMessage(`BeenButton/toggleBeenTo: ${ visited ? "quick_remove" : "quick_add" } w/ empty osm_uid`, { extra: { osm_uid } });
        return;
        // Note, the button will stay disabled
    }
    
    const api = (been === true) ? '/_api/nominatim/quick-remove' : 
                (been === false) ? '/_api/nominatim/quick-add' :
                undefined;
    const op = visited ? 'Remove' : 'Add';

    const formData = new FormData();
    formData.append('osm_uid', osm_uid);
    formData.append('page', window.location.pathname);

    fetch(api, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
    })
    .then(handleFetch, handleFetchReject)
    .then((json) => {
        if(json?.error) {
            error = json.error;
            // Note, the button will stay disabled
        } else if(json?.status == 200) {
            if(json?.delta) {
                //console.log(`activeUserData.data=${JSON.stringify(activeUserData.data)}`);
                //console.log(`delta(data)=${JSON.stringify(json?.delta)}`);

                patchActiveUserData(json.delta);

                //console.log(`activeUserData.data (patched)=${JSON.stringify(activeUserData.data)}`);

                const visitedPointsSource = map.getSource('beenEW_visited_points');
                if(visitedPointsSource) {
                    visitedPointsSource.setData(activeUserData.data);
                }

                if(json?.invalidate) {
                    console.log("BeenButton: Got json.invalidate, running invalidateAll()");
                    Sentry.captureMessage("BeenButton: Got json.invalidate, running invalidateAll()");
                    invalidateAll();
                }

                visited = !visited;
                disabled = false;

                if(flyToOnBeen && map) {
                    map_flyTo(map, {center: flyToOnBeen, zoom: 10, speed: 15 }, {doNotFlyIfOnScreen: true});
                }
            } else {
                Sentry.captureMessage("BeenButton/toggleBeenTo: Was unable to load patch data (no .delta)", { extra: { json } });
                console.log("BeenButton/toggleBeenTo: Was unable to load patch data (no .delta)");
                error = `${op} failed`;
                // Note, the button will stay disabled
            }
        } else {
            Sentry.captureMessage("toggle_beento: Was unable to load patch data (status != 200)", { extra: { json } });
            console.log("toggle_beento: Was unable to load patch data (status != 200)");
            error = `Status ${json?.status}`;
            // Note, the button will stay disabled
        }
        tick().then(()=> {
            the_button.focus();
        });
    });
}
    */

//export async function toggle_visit(map, action, {osm_uid, visit_id, hide, transit, flyToOnBeen, page} = {}) {
export async function toggle_visit(map, action, params = {}) {
    const formData = new FormData();
    formData.append('action', action);
    formData.append('page', window.location.pathname);

    // uuid is unused now (capacitor?)
    for (const name of ['osm_uid', 'visit_id', 'hide', 'transit', 'token', 'uuid']) {
        const value = params[name];
        if (value !== null && value !== undefined) {
            formData.append(name, value);
        }
    }

    try {
        const response = await fetch(server_name('/_api/nominatim/visit'), {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
            ...(params.token && { headers: { Authorization: `Bearer ${params.token}` } })
        });

        const json = await handle_fetch(response);

        if (json?.error) {
            return json.error;
        }

        if (json?.status !== 200) {
            Sentry.captureMessage("toggle_visit: API response status != 200", { extra: { action, params, json } });
            return `Status ${json?.status}`;
        }

        // Update user data
        const ret = await patchActiveUserDataAsync(json?.delta, params.token);

        if(!ret) {
            return `Failed to ${action}`;
        }

        // Handle invalidation
        if (json?.invalidate) {
            //console.log("toggle_visit: Got json.invalidate, running invalidateAll()");
            //Sentry.captureMessage("toggle_visit: Got json.invalidate, running invalidateAll()");
            invalidateAll();
        }

        // Fly to location (if enabled and available)
        if (params?.flyToOnBeen && map) {
            map_flyTo(map, { center: params.flyToOnBeen, zoom: 10, speed: 15 }, { doNotFlyIfOnScreen: true });
        }

    } catch (err) {
        console.error("Error in toggle_visit: ", err);
        return "Network error. Please try again.";
    }

    return null;
}


// map.flyTo which does not zoom out
export function map_flyTo(map, x, {doNotFlyIfOnScreen} = {}) {
    if(map.getZoom() > x?.zoom) {
        // if the map's zoom is higher then the requested x.zoom, 
        // and the new dest x.center is inside the map bounds, 
        // don't change zoom (because it may cause a distracting zoom out)
        if(map.getBounds().contains(x.center)) {
            if(!doNotFlyIfOnScreen) {
                map.flyTo({center: x.center, essential: true});
            }
        } else {
            map.flyTo({center: x.center, essential: true, speed: (x?.speed ? x.speed*2 : 30) });
        }
    } else {
        if(!(doNotFlyIfOnScreen && map.getBounds().contains(x.center))) {
            map.flyTo(x);
        }
    }
}


export function map_disable_rotation(map) {
    // disable map rotation using right click + drag
    map.dragRotate.disable();

    // disable map rotation using keyboard
    map.keyboard.disable();

    // disable map rotation using touch rotation gesture
    map.touchZoomRotate.disableRotation();

    // Lock pitch (tilt) by disabling pitch gestures
    map.touchPitch.disable();
}


export function map_flyToDataTarget(map, e) {
    if(map) {
        let reg = e.currentTarget.getAttribute("data-target");
        if(reg) {
            const bbox = reg_bbox?.[reg]?.bbox;
            if(Array.isArray(bbox)) {
                map.fitBounds(bbox, {
                    padding: {
                        top: 30,
                        bottom: 30,
                        left: 30,
                        right: 30,
                    },
                    speed: 15,
                });
            } else {
                console.log('map_flyToDataTarget: No bbox');
                Sentry.captureMessage('map_flyToDataTarget: No bbox', { extra: { reg }});
            }
        } else {
            console.log('map_flyToDataTarget: No data-target');
            Sentry.captureMessage('map_flyToDataTarget: No data-target');
        }
    } else {
        console.log('map_flyToDataTarget: No map');
        Sentry.captureMessage('map_flyToDataTarget: No map');
    }
}


// b is object or string with css id
export function button_disable(b, {is_loading = true} = {}) {
    const b_orig = b;
    if (typeof b === 'string') {
        b = document.getElementById(b);
    }
    if(b) {
        if(is_loading)
            b.classList.add('is-loading');
        else 
            b.classList.remove('is-loading');
        b.disabled = true;
    } else {
        Sentry.captureMessage("button_disable: b is falsy", { extra: { b, b_orig }});
    }
}
  

// b is object or string with css id
export function button_enable(b) {
    const b_orig = b;
    if (typeof b === 'string') {
        b = document.getElementById(b);
    }
    if(b) {
        b.classList.remove('is-loading');
        b.disabled = false;
    } else {
        Sentry.captureMessage("button_enable: b is falsy", { extra: { b, b_orig }});
    }
}


export function city_search_success_function(map, data, {flyToFirst, focusFirst, token, capacitor} = {}) {
    if(!Array.isArray(data)) {
        Sentry.captureMessage("city_search_success_function: data is not array", { extra: { data }});
        return;
    }

    const table = document.getElementById("t_cities");

    if(table) {
	    table.innerHTML = "";
    }

    if(data.length < 1) {
        mapDisplayMessage({map, msg: 'Click map labels to add cities,<br>or zoom out to select from popular cities'});
        return;
    }

    // if the map's zoom is high (>6), before flying to the first Nominatim's response (most popular place) 
    // try to check if there is a found place inside the map's view (if any) and fly there instead 
    if(flyToFirst && map && map.getZoom() > 6) {
        data.forEach(function(element, index) {
            if(flyToFirst && map.getBounds().contains(element.coordinates)) {
                map_flyTo(map, {center: element.coordinates, essential: true });
                flyToFirst = false;
            }
        });
    }
    
    let cnt = 0;

    if (data.length == 1 && data[0].type == 'continent' && !data[0]?.visited) {
        const continents_e = {
            "Europe": "🥐🥖🥨",
            "North America": "🍁🗽🌵",
            "Asia": "🏔️⛩️🌊",
            "Africa": "🦁🐘🦒",
            "South America": "🦙💃",
            "Australia": "🦘",
            "Oceania": "🏝️🗿",
            "Antarctica": "🇦🇶"
        }

        mapDisplayMessage({map, msg: `To add ${data[0].name}${continents_e?.[data[0].name] ? (' '+continents_e[data[0].name]) : ''} zoom in and add places!`});
        return;
    }
    
    data.forEach(function(element, index) {
        if(cnt === 0) {
            if(flyToFirst) {
                map_flyTo(map, {center: element.coordinates, zoom: 6, speed: 10 });
            }
        }

        let cell = table.insertRow().insertCell();
        mount(FlyableText, {target: cell, props: {
                                                map,
                                                text: element.label,
                                                lon: element.coordinates[0],
                                                lat: element.coordinates[1],
                                                icon: element?.icon ?? '' }});

        if(!element?.osm_uid || element?.osm_uid == "") 
            Sentry.captureMessage("city_search_success_function is creating BeenButton with an empty osm_uid", { extra: { element }});

        mount(BeenButton, {
            target: cell, props: {
                map,
                token,
                osm_uid: element.osm_uid,
                label: element.name,
                flyToOnBeen: element.coordinates,
                autofocus: (focusFirst && cnt===0),
                capacitor,
                visitors: element.visitors,
            }
        });

        cell.className = "level is-mobile";

		++cnt;
	});

    applyTooltips();
}


export function find_user_bounds(map_settings, user_data) {
    let bbox = [[5,-65],[10,80]]; // default;

    if(typeof map_settings === 'object') {
        if(map_settings?.view === 'been') {
            //!!bbox = been_box;
            /// && been_box && been_box.length == 2
            if(user_data?.stats?.most_western?.lon && user_data?.stats?.most_southern?.lat &&
                user_data?.stats?.most_eastern?.lon && user_data?.stats?.most_nothern?.lat) {
                    bbox = [[Math.max(user_data.stats.most_western.lon-5, -180), Math.max(user_data.stats.most_southern.lat-5, -90)],
                            [Math.min(user_data.stats.most_eastern.lon+5,  180), Math.min(user_data.stats.most_nothern.lat+5,   90)]];
            }
        } else if(map_settings?.view === 'europe') {
            bbox = [[-30,25],[63,72]]
        } else if(map_settings?.view === 'custom' && 'map_bounds' in map_settings) {
            bbox = map_settings['map_bounds'];
        }
    }
    return bbox;
}


export function map_settings_add_defaults(map_settings = {}, map_settings_override = {}) {
    const defaults = {
            "style": "streets",
            "view": "world",
            "show-locations": "points",
            "circle-color": '#e55e5e',
            "circle-stroke-color": "#ffffff",
            "heatmap-color": "1",
            "highlight-countries": "yes",
            "highlight-color": '#f6a192',
        };

        return { ...defaults, ...map_settings, ...map_settings_override };
}


export function map_style_url(map_style) {
    let style_url = false;
    switch(map_style) {
        ///
        case 'americana':
            style_url = server_name('/maps/styles/americana/style.json'); // USES AMERICANA CLOUD!!!
            break;

        ///
        case 'charted-territory':
            style_url = 'https://basemaps-api.arcgis.com/arcgis/rest/services/styles/ArcGIS:ChartedTerritory?type=style&token=AAPK7c58c668212c48f88de20d9ed660f2acyT2xljTdtM5Q-s6hbYjQS2kdLgbXu3eM9mH-vcaqmDFJX-OqfE2u2QMKpt-MHmwL';
            break;

        ///
        case 'bright':
            //style_url = `https://${PUBLIC_MY_MAPTILER}/api/maps/bright/style.json`;
            style_url = server_name('/maps/styles/bright/style.json');
            break;

        ///
        case 'satellite-hybrid':
            //style_url = `https://${PUBLIC_MY_MAPTILER}/api/maps/satellite-hybrid/style.json`;
            style_url = server_name('/maps/styles/satellite-hybrid/style.json'); // NO LICENSE!!!
            break;

        ///
        case 'basic':
            //style_url = `https://${PUBLIC_MY_MAPTILER}/api/maps/basic/style.json`;
            //style_url = `https://maps.beeneverywhere.net/styles/basic/style.json`;
            //style_url = server_name('/maps/styles/basic/style.json');
        case 'grayscale':
            style_url = server_name('/maps/styles/grayscale/style.json');
            break;

        ///
        case 'topo':
            //style_url = `https://${PUBLIC_MY_MAPTILER}/api/maps/topo/style.json`;
            //style_url = server_name('/maps/styles/topo/style.json'); // NO LICENSE!!!
        case 'terrain':
            style_url = server_name('/maps/styles/terrain/style.json'); // USES MAPTILER KEY!!!
            break;

        ///
        case 'fiord-color':
            //style_url = `https://${PUBLIC_MY_MAPTILER}/api/maps/fiord-color/style.json`;
            style_url = server_name('/maps/styles/fiord-color/style.json');
            break;

        ///
        case 'openstreetmap':
            //style_url = `https://maps.beeneverywhere.net/styles/openstreetmap/style.json`;
            style_url = server_name('/maps/styles/openstreetmap/style.json');
            break;


    /*
        case 'maptiler-streets':
            style_url = 'https://api.maptiler.com/maps/streets/style.json?key=c3fjVLV2xCYF89g2Z877';
            break;
    */
        case 'beeneverywhere':
        case 'streets':
        default:
            //style_url = `https://${PUBLIC_MY_MAPTILER}/api/maps/streets/style.json`;
            //style_url = `https://maps.beeneverywhere.net/styles/streets/style.json`;
            style_url = server_name('/maps/styles/streets/style.json'); // NO LICENSE!!!
            //style_url = '/maps/styles/streets/style.json'; // NO LICENSE!!!
            break;
    }
    return style_url;
}


export function keydownWrapper(fn) {
    return function (e) {
        if(e.code === 'Space' || e.code === 'Enter') {
            e.preventDefault();
            fn.call(this, e);
        }
    };
}


// falsy is ok, otherwise return a text of a error
export function check_new_password(password, allow_empty) {
    if((!allow_empty || password.length > 0) && password.length < 8) {
        return "Password must be at least 8 characters long";
    } else if(password !== password.trim()) {
        return "Leading or trailing spaces are not allowed in password";
    } else {
        return undefined;
    }
}


// checkNewPassword() has side effects:
// it looks for a new password in new-password element;
// it puts (removes) error message to error_in_password element
// and sets (removes) is-danger class

export function checkNewPassword(allow_empty) {
    const passEl = document.getElementById("new-password");
    if(!passEl) return false;

    let error = check_new_password(passEl.value, allow_empty);
    const errorEl = document.getElementById("error_in_password");

    if(error) {
        passEl.classList.add("is-danger");
        if(errorEl) errorEl.innerHTML = error;
    } else {
        passEl.classList.remove("is-danger");
        if(errorEl) errorEl.innerHTML = '';
    }

    return !error;

}

/*
export function checkNewPassword(allow_empty) {
    let error = "";
    const pass1 = document.getElementById("new-password");
    
    if((!allow_empty || pass1.value.length > 0) && pass1.value.length < 8) {
        error = document.getElementById("error_in_password").innerHTML = "Password must be at least 8 characters long";
        pass1.classList.add("is-danger");
        return false;
    } else if(pass1.value !== pass1.value.trim()) {
        error = document.getElementById("error_in_password").innerHTML = "Leading or trailing spaces are not allowed";
        pass1.classList.add("is-danger");
        return false;
    } else {
        document.getElementById("error_in_password").innerHTML = "";
        pass1.classList.remove("is-danger");
        return true;
    }
}
*/

export function handleLogout(e) {
    if(e) {
        e.preventDefault();
    }

    const formData = new FormData();
    formData.append('logout', 'yes');

    //fetch('/api', {
    fetch('/_api/auth/logout', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
    })
    .then(handleFetch, handleFetchReject)
    .then((data) => { 
        if(data?.error && data.error != 'Not logged in') {
            Sentry.captureMessage("Error logging out", { extra: { data }});
        }
        invalidateAll();
    }); 
}


export function getFormState(id) {
    if (!document) return;

    const form = document.getElementById(id);

    if(!form) return false;

    const formData = new FormData(form);
    let form_state = {};

    for (const element of form.elements) {
        if(element.nodeName.toUpperCase() == "BUTTON" && element.hasAttribute('name') && element.classList.contains("is-selected")) {
            form_state[element.name] = element.value;
        }
    }

    formData.forEach((value, key) => {
        // Reflect.has in favor of: form_state.hasOwnProperty(key)
        if(!Reflect.has(form_state, key)){
            form_state[key] = value;
            return;
        }
        if(!Array.isArray(form_state[key])){
            form_state[key] = [form_state[key]];
        }
        form_state[key].push(value);
    });

    return form_state;
}

// Unused? capacitor?
export function handleAppleLoginWeb(e) {
    e.preventDefault();
    const the_button = e.currentTarget;
    button_disable(the_button);
    const url = new URL("https://appleid.apple.com/auth/authorize");

    url.searchParams.set("client_id", PUBLIC_APPLE_CLIENT_ID_WEB);
    url.searchParams.set("redirect_uri", server_name('/_api/auth/apple-login'));
    url.searchParams.set("response_type", "code");
    url.searchParams.set("scope", "name email");
    url.searchParams.set("response_mode", "form_post");

    // Fallback in case user cancels and stays on page
    setTimeout(() => {
        button_enable(the_button);
    }, 6000);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            // If user comes back and the button is still disabled
            if (the_button && the_button.disabled) {
                button_enable(the_button);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    });


    window.location = url.toString();
}
